import * as React from "react";
import { ExternalLink, Globe, Search } from "lucide-react";
import ReactMarkdown from "react-markdown";
import clsx from "clsx";
import type { OpenChatMessage, WebSearchSource, WebSearchSupport } from "@/types/open-chat";
import { getFaviconUrl } from "@/helpers/images";
import { safeExternalUrl } from "@/helpers/safeExternalUrl";

interface Props {
  messages: OpenChatMessage[];
  activeSourceIds?: number[];
  activeMessageId?: string | null;
}

const getExcerptsForSource = (source: WebSearchSource, supports: WebSearchSupport[]): string[] =>
  supports.filter(s => s.source_ids.includes(source.id)).map(s => s.text);

export const SidebarSources = ({ messages, activeSourceIds = [], activeMessageId }: Props) => {
  const activeRef = React.useRef<HTMLAnchorElement>(null);
  const messagesWithSources = messages.filter(m => m.searchSources?.sources?.length);

  React.useEffect(() => {
    if (activeSourceIds.length > 0 && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeSourceIds]);

  if (!messagesWithSources.length) {
    return <div className="p-4 text-sm text-muted-foreground">No web search sources yet.</div>;
  }

  const activeMessages = activeMessageId
    ? messagesWithSources.filter(m => m.id === activeMessageId)
    : messagesWithSources;

  const allSources = activeMessages.flatMap(m => m.searchSources!.sources);
  const allSupports = activeMessages.flatMap(m => m.searchSources?.supports ?? []);
  const allQueries = [...new Set(activeMessages.flatMap(m => m.searchSources?.search_queries ?? []))];

  // Deduplicate sources by URL
  const uniqueSources = allSources.reduce<WebSearchSource[]>((acc, source) => {
    if (!acc.some(s => s.url === source.url)) acc.push(source);
    return acc;
  }, []);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      {allQueries.length > 0 && (
        <div className="border-b border-desert-sand-200 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {allQueries.map(query => (
              <a
                key={query}
                href={`https://www.google.com/search?q=${encodeURIComponent(query)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:bg-ivory-mist-50 inline-flex items-center gap-1.5 rounded-full border border-desert-sand-200 bg-white px-3 py-1.5 text-xs text-foreground shadow-sm transition-all hover:border-desert-sand-300 hover:shadow-md"
              >
                <Search size={12} className="shrink-0 text-muted-foreground" />
                <span className="max-w-[220px] truncate">{query}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="mb-4 flex items-center gap-2">
          <Globe size={16} className="text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Sources ({uniqueSources.length})
          </span>
        </div>

        <div className="space-y-2">
          {uniqueSources.map((source, index) => {
            const safeUrl = safeExternalUrl(source.url);
            const favicon = getFaviconUrl(safeUrl ?? null);
            const excerpts = getExcerptsForSource(source, allSupports);
            const isActive = activeSourceIds.includes(source.id);

            return (
              <a
                key={`${source.id}-${source.url}`}
                ref={isActive ? activeRef : undefined}
                href={safeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(
                  "group flex items-start gap-3 rounded-lg border p-3 shadow-sm transition-all hover:shadow-md",
                  {
                    "border-brand-gold-300 bg-brand-gold-50 ring-2 ring-brand-gold-200": isActive,
                    "border-desert-sand-200 bg-white hover:border-desert-sand-300": !isActive,
                  },
                )}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ivory-mist-100">
                  {favicon ? (
                    <img src={favicon} alt="" className="h-4 w-4 rounded-sm" />
                  ) : (
                    <Globe size={16} className="text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-foreground group-hover:text-brand-gold-700">
                      {source.title || `Source ${index + 1}`}
                    </span>
                    <ExternalLink
                      size={11}
                      className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  </div>
                  {excerpts.length > 0 && (
                    <div className="mt-1 line-clamp-3 text-xs leading-relaxed text-muted-foreground [&_em]:italic [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-foreground">
                      <ReactMarkdown>{excerpts.join(" ")}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};
